/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Briefcase, Pencil, Plus, X } from 'lucide-react';
import SingleViewLayout from 'layouts/SingleViewLayout';
import { useParams } from 'react-router-dom';
import useGetSingleOffence from 'hooks/offences/usegetSingleOffence';
import useUpdateOffence from 'hooks/offences/useUpdateOffence';
import useGetSlabRates from 'hooks/offences/useGetSlabRates';
import useAddSlabRate from 'hooks/offences/useAddSlabRate';
import useUpdateSlabRate from 'hooks/offences/useUpdateSlabRate';
import FineDetailsSkeleton from 'components/common/skeletons/FineDetailsSkeleton';
import NoDataHandling from 'components/shared/NoData/NoDataHandling';
import moment from 'moment';
import { Bounce, toast } from 'react-toastify';
import { SlabRate } from 'utils/types';

interface InfoItemProps {
  label: string;
  value?: string | number;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium">{value ?? '—'}</p>
  </div>
);

const initialSlabForm = {
  lowerBound: 0,
  upperBound: 0,
  amount: 0,
};

type ViolationForm = {
  name: string;
  offenceLabel: string;
  sectionAct: string;
  amount: number;
  isSlabRateApplicable: '' | 'Y' | 'N';
};

type SlabForm = typeof initialSlabForm;

type SubmitHandler = (
  e: React.SyntheticEvent<HTMLFormElement>,
) => void | Promise<void>;

const toastOptions = {
  position: 'top-right' as const,
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  theme: 'light' as const,
  transition: Bounce,
};

const getSlabForm = (slab?: SlabRate | null): SlabForm => {
  if (!slab) return initialSlabForm;

  return {
    lowerBound: slab.lowerBound,
    upperBound: slab.upperBound,
    amount: slab.amount,
  };
};

const getStatusClasses = (status: string) => {
  if (status === 'Active') return 'bg-green-100 text-green-800';
  return 'bg-red-100 text-red-800';
};

interface ModalBackdropProps {
  disabled: boolean;
  onClose: () => void;
}

const ModalBackdrop: React.FC<ModalBackdropProps> = ({ disabled, onClose }) => (
  <button
    type="button"
    aria-label="Close modal"
    disabled={disabled}
    onClick={onClose}
    className="absolute inset-0 bg-black/40 disabled:cursor-default"
  />
);

interface EditViolationModalProps {
  form: ViolationForm;
  isPending: boolean;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
  onClose: () => void;
  onSubmit: SubmitHandler;
}

const EditViolationModal: React.FC<EditViolationModalProps> = ({
  form,
  isPending,
  onChange,
  onClose,
  onSubmit,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <ModalBackdrop disabled={isPending} onClose={onClose} />
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Edit Violation</h2>
        <button
          onClick={onClose}
          disabled={isPending}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Offence Name
          </label>
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Offence Label
          </label>
          <input
            name="offenceLabel"
            value={form.offenceLabel}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Section Act
          </label>
          <input
            name="sectionAct"
            value={form.sectionAct}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount (KES)
          </label>
          <input
            name="amount"
            type="number"
            min={0}
            value={form.amount}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slab Rate Applicable
          </label>
          <select
            name="isSlabRateApplicable"
            value={form.isSlabRateApplicable}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="" disabled>
              Select option
            </option>
            <option value="Y">Yes</option>
            <option value="N">No</option>
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  </div>
);

interface SlabRateModalProps {
  form: SlabForm;
  isPending: boolean;
  selectedSlab: SlabRate | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  onSubmit: SubmitHandler;
}

const SlabRateModal: React.FC<SlabRateModalProps> = ({
  form,
  isPending,
  selectedSlab,
  onChange,
  onClose,
  onSubmit,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <ModalBackdrop disabled={isPending} onClose={onClose} />
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">
          {selectedSlab ? 'Edit Slab Rate' : 'Add Slab Rate'}
        </h2>
        <button
          onClick={onClose}
          disabled={isPending}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="slabLowerBound"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Lower Bound
          </label>
          <input
            id="slabLowerBound"
            name="lowerBound"
            type="number"
            min={0}
            value={form.lowerBound}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="slabUpperBound"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Upper Bound
          </label>
          <input
            id="slabUpperBound"
            name="upperBound"
            type="number"
            min={0}
            value={form.upperBound}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="slabAmount"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Amount (KES)
          </label>
          <input
            id="slabAmount"
            name="amount"
            type="number"
            min={0}
            value={form.amount}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : 'Save Slab'}
          </button>
        </div>
      </form>
    </div>
  </div>
);

interface SlabRatesSectionProps {
  slabs?: SlabRate[];
  slabsLoading: boolean;
  onAddSlab: () => void;
  onEditSlab: (slab: SlabRate) => void;
}

const SlabRatesSection: React.FC<SlabRatesSectionProps> = ({
  slabs,
  slabsLoading,
  onAddSlab,
  onEditSlab,
}) => {
  let content = (
    <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
      <p className="text-sm text-gray-500">
        No slab rates have been configured for this violation.
      </p>
    </div>
  );

  if (slabsLoading) {
    content = <p className="text-sm text-gray-500">Loading slab rates...</p>;
  }

  if (slabs && slabs.length > 0) {
    content = (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3">Lower Bound</th>
              <th className="px-4 py-3">Upper Bound</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {slabs.map((slab) => {
              const statusClasses = getStatusClasses(slab.status);
              const formattedAmount = Number(slab.amount).toLocaleString(
                'en-KE',
                {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                },
              );

              return (
                <tr key={slab.id} className="text-sm text-gray-700">
                  <td className="px-4 py-3">{slab.lowerBound}</td>
                  <td className="px-4 py-3">{slab.upperBound}</td>
                  <td className="px-4 py-3">KES {formattedAmount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${statusClasses}`}
                    >
                      {slab.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onEditSlab(slab)}
                      aria-label={`Edit slab rate ${slab.lowerBound} to ${slab.upperBound}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-6 w-6 text-gray-600" />
          <h2 className="text-xl font-bold text-gray-800">Slab Rates</h2>
        </div>
        <button
          onClick={onAddSlab}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:scale-95 transition-all duration-150"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Slab
        </button>
      </div>
      <div className="p-6">{content}</div>
    </div>
  );
};

const ViolationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: offence, isLoading: offenceLoading } = useGetSingleOffence(
    id || '',
  );
  const { mutateAsync: updateOffence, isPending } = useUpdateOffence();
  const { data: slabs, isLoading: slabsLoading } = useGetSlabRates(
    offence?.id,
    offence?.slabApplicable === 'Y',
  );
  const { mutateAsync: addSlabRate, isPending: isAddingSlab } =
    useAddSlabRate();
  const { mutateAsync: updateSlabRate, isPending: isUpdatingSlab } =
    useUpdateSlabRate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSlabModalOpen, setIsSlabModalOpen] = useState(false);
  const [selectedSlab, setSelectedSlab] = useState<SlabRate | null>(null);
  const [slabForm, setSlabForm] = useState(initialSlabForm);
  const [form, setForm] = useState<ViolationForm>({
    name: '',
    offenceLabel: '',
    sectionAct: '',
    amount: 0,
    isSlabRateApplicable: '' as '' | 'Y' | 'N',
  });

  const openModal = () => {
    if (!offence) return;
    setForm({
      name: offence.offenceTitle,
      offenceLabel: offence.offenceLabel,
      sectionAct: offence.sectionAct,
      amount: offence.amount,
      isSlabRateApplicable: (offence.slabApplicable as '' | 'Y' | 'N') ?? '',
    });
    setIsModalOpen(true);
  };

  const openSlabModal = (slab?: SlabRate) => {
    setSelectedSlab(slab ?? null);
    setSlabForm(getSlabForm(slab));
    setIsSlabModalOpen(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'amount' ? Number.parseFloat(value) || 0 : value,
    }));
  };

  const handleSlabChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSlabForm((prev) => ({
      ...prev,
      [name]: Number.parseFloat(value) || 0,
    }));
  };

  const handleSubmit: SubmitHandler = async (e) => {
    e.preventDefault();
    if (!offence) return;

    try {
      await updateOffence({ id: offence.id, ...form });
      toast.success('Violation updated successfully', toastOptions);
      setIsModalOpen(false);
    } catch {
      toast.error('There was an error updating the violation', toastOptions);
    }
  };

  const handleSlabSubmit: SubmitHandler = async (e) => {
    e.preventDefault();
    if (!offence) return;

    const payload = {
      offenceId: offence.id,
      ...slabForm,
    };

    try {
      if (selectedSlab) {
        await updateSlabRate({ id: selectedSlab.id, ...payload });
        toast.success('Slab rate updated successfully', toastOptions);
      } else {
        await addSlabRate(payload);
        toast.success('Slab rate added successfully', toastOptions);
      }

      setIsSlabModalOpen(false);
      setSelectedSlab(null);
      setSlabForm(initialSlabForm);
    } catch {
      toast.error('There was an error saving the slab rate', toastOptions);
    }
  };

  if (offenceLoading) return <FineDetailsSkeleton />;
  if (!offence)
    return (
      <NoDataHandling content="No violation details found for the specified ID." />
    );

  const isSlabPending = isAddingSlab || isUpdatingSlab;

  return (
    <SingleViewLayout title="Violation Details">
      {isModalOpen && (
        <EditViolationModal
          form={form}
          isPending={isPending}
          onChange={handleChange}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {isSlabModalOpen && (
        <SlabRateModal
          form={slabForm}
          isPending={isSlabPending}
          selectedSlab={selectedSlab}
          onChange={handleSlabChange}
          onClose={() => setIsSlabModalOpen(false)}
          onSubmit={handleSlabSubmit}
        />
      )}

      <div className="relative">
        <div className="relative z-0">
          <div className="lg:p-6 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="lg:px-6 px-2 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  Violation #{offence.id.toString().slice(-6).toUpperCase()}
                </h2>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-4 py-1 rounded-full text-sm font-medium ${
                      offence.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {offence.status}
                  </span>
                  <button
                    onClick={openModal}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:scale-95 transition-all duration-150"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoItem
                    label="Offence Title"
                    value={offence.offenceTitle}
                  />
                  <InfoItem
                    label="Offence Label"
                    value={offence.offenceLabel}
                  />
                  <InfoItem label="Section Act" value={offence.sectionAct} />
                  <InfoItem
                    label="Date Created"
                    value={moment(offence.dateCreated).format(
                      'Do MMMM YYYY h:mm a',
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Offence Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <Briefcase className="h-6 w-6 text-gray-600" />
                  <h2 className="text-xl font-bold text-gray-800">
                    Offence Details
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InfoItem
                      label="Amount"
                      value={`KES ${offence.amount.toLocaleString()}`}
                    />
                    <InfoItem
                      label="Slab Applicable"
                      value={offence.slabApplicable === 'Y' ? 'Yes' : 'No'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {offence.slabApplicable === 'Y' && (
              <SlabRatesSection
                slabs={slabs}
                slabsLoading={slabsLoading}
                onAddSlab={() => openSlabModal()}
                onEditSlab={openSlabModal}
              />
            )}
          </div>
        </div>
      </div>
    </SingleViewLayout>
  );
};

export default ViolationDetails;
